// Fleets in flight on the map (spec 15c §3.5): their path and where they are now. Own and alliance
// fleets walk their route legs in time; detections only know the network exit and the target.
import type { OwnFleet, VisibleFleet } from '@stars-and-ships/api-types';
import type { Point } from './camera';

export type FleetRelation = 'own' | 'alliance' | 'detected';

export interface MapFleet {
  id: string;
  relation: FleetRelation;
  /** From start to target; null when the way is unknown (a recalled fleet). */
  path: Point[] | null;
  target: Point;
  departedAt: number | null;
  arrivesAt: number;
  /** Seconds of each path segment (route legs); null = by segment length. */
  legSeconds: number[] | null;
  item: VisibleFleet;
}

export interface MarkerPose {
  point: Point;
  angle: number;
}

type Way = { path: Point[] | null; legSeconds: number[] | null } | 'skip';

function wayOf(fleet: OwnFleet, systems: ReadonlyMap<string, Point>, target: Point): Way {
  const route = fleet.route;
  if (route && route.length > 0) {
    const ids = [route[0]?.from as string, ...route.map((leg) => leg.to)];
    const path = ids.map((id) => systems.get(id));
    if (path.some((p) => !p)) return 'skip';
    return { path: path as Point[], legSeconds: route.map((leg) => leg.seconds) };
  }
  const origin = fleet.originSystemId ? systems.get(fleet.originSystemId) : undefined;
  if (origin && fleet.originSystemId !== fleet.targetSystemId) {
    return { path: [origin, target], legSeconds: null };
  }
  return { path: null, legSeconds: null };
}

export function mapFleets(
  items: readonly VisibleFleet[],
  systems: ReadonlyMap<string, Point>
): MapFleet[] {
  return items.flatMap((item): MapFleet[] => {
    if (item.relation === 'detected') {
      const d = item.detection;
      const exit = systems.get(d.exitSystemId);
      const target = systems.get(d.targetSystemId);
      if (!exit || !target) return [];
      return [
        {
          id: d.fleetId,
          relation: 'detected',
          path: d.exitSystemId === d.targetSystemId ? null : [exit, target],
          target,
          departedAt: null,
          arrivesAt: Date.parse(d.arrivesAt),
          legSeconds: null,
          item,
        },
      ];
    }
    const f = item.fleet;
    const target = f.targetSystemId ? systems.get(f.targetSystemId) : undefined;
    if (!target || !f.arrivesAt) return [];
    const way = wayOf(f, systems, target);
    if (way === 'skip') return [];
    return [
      {
        id: f.id,
        relation: item.relation,
        path: way.path,
        target,
        departedAt: f.departedAt ? Date.parse(f.departedAt) : null,
        arrivesAt: Date.parse(f.arrivesAt),
        legSeconds: way.legSeconds,
        item,
      },
    ];
  });
}

const angleOf = (a: Point, b: Point) => Math.atan2(b.y - a.y, b.x - a.x);

/** The marker pose and the path segment it is on (null = at the target or not moving). */
function poseOf(fleet: MapFleet, now: number): MarkerPose & { segment: number | null } {
  const path = fleet.path;
  const lastAngle =
    path && path.length >= 2
      ? angleOf(path[path.length - 2] as Point, path[path.length - 1] as Point)
      : 0;
  const atTarget = { point: fleet.target, angle: lastAngle, segment: null };
  if (!path || path.length < 2 || fleet.relation === 'detected' || fleet.departedAt === null) {
    return atTarget;
  }
  const weights =
    fleet.legSeconds ??
    path.slice(1).map((p, i) => {
      const prev = path[i] as Point;
      return Math.hypot(p.x - prev.x, p.y - prev.y);
    });
  const total = weights.reduce((sum, w) => sum + w, 0);
  const flight = fleet.arrivesAt - fleet.departedAt;
  const progress = flight > 0 ? Math.min(1, Math.max(0, (now - fleet.departedAt) / flight)) : 1;
  if (total <= 0 || progress >= 1) return atTarget;
  let remaining = progress * total;
  for (let i = 0; i < weights.length; i++) {
    const w = weights[i] as number;
    const a = path[i] as Point;
    const b = path[i + 1] as Point;
    if (remaining <= w || i === weights.length - 1) {
      const t = w > 0 ? Math.min(1, remaining / w) : 1;
      return {
        point: { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t },
        angle: angleOf(a, b),
        segment: i,
      };
    }
    remaining -= w;
  }
  return atTarget;
}

/** Where the fleet is at `now`. Detections and fleets without a known way sit at the target. */
export function fleetMarker(fleet: MapFleet, now: number): MarkerPose {
  const { point, angle } = poseOf(fleet, now);
  return { point, angle };
}

/**
 * The way still ahead of a fleet (Tim, 15c feedback): from its marker to the target. Detections
 * have no known position, so their whole known path stays; null when nothing is left to draw.
 */
export function remainingPath(fleet: MapFleet, now: number): Point[] | null {
  const path = fleet.path;
  if (!path || path.length < 2) return null;
  if (fleet.relation === 'detected' || fleet.departedAt === null) return path;
  const pose = poseOf(fleet, now);
  if (pose.segment === null) return null;
  return [pose.point, ...path.slice(pose.segment + 1)];
}

/** Length of a polyline. */
export function pathLength(points: readonly Point[]): number {
  let sum = 0;
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1] as Point;
    const b = points[i] as Point;
    sum += Math.hypot(b.x - a.x, b.y - a.y);
  }
  return sum;
}

/**
 * Where a fleet flies from and to, for the fleet card. A returning fleet's origin is its home (the
 * same as its target), so it comes from where its return route starts; a recalled fleet has no route
 * and its turning point is unknown.
 */
export function fleetEnds(fleet: OwnFleet): {
  from: string | null;
  to: string | null;
  recalled: boolean;
} {
  if (fleet.status !== 'returning') {
    return { from: fleet.originSystemId, to: fleet.targetSystemId, recalled: false };
  }
  const start = fleet.route?.[0]?.from;
  return start
    ? { from: start, to: fleet.targetSystemId, recalled: false }
    : { from: null, to: fleet.targetSystemId, recalled: true };
}

/** Pixels a detection's marker sits back from its target, so it is visible and tappable. */
export const DETECTED_INSET_PX = 14;

/** The marker's world point at a camera scale: a detection is drawn just short of its target. */
export function markerPoint(fleet: MapFleet, now: number, scale: number): Point {
  const point = fleetMarker(fleet, now).point;
  const path = fleet.path;
  if (fleet.relation !== 'detected' || !path || path.length < 2) return point;
  const from = path[path.length - 2] as Point;
  const length = Math.hypot(point.x - from.x, point.y - from.y);
  if (length === 0) return point;
  const back = Math.min(length, DETECTED_INSET_PX / scale) / length;
  return { x: point.x + (from.x - point.x) * back, y: point.y + (from.y - point.y) * back };
}
